import { MoreVert } from '@mui/icons-material';
import { Menu, MenuItem, Paper } from '@mui/material';
import { GridActionsCellItem, GridValidRowModel } from '@mui/x-data-grid';
import React, { useCallback, useEffect, useState } from 'react';
import {
  DbDeliverableType,
  HandlersCreateProgramDeliverableTypeRequest,
  useDeleteApiProgramsByIdDeliverableTypesAndDeliverableTypeIdMutation,
  useLazyGetApiProgramsDeliverableTypesQuery,
} from '../../../../../../app/athenaApiGen';
import DeleteConfirmation from '../../../../../../components/Modals/DeleteConfirmation';
import { StyledDataGrid } from '../../../../../../shared-theme/StyledComponents';
import {
  DEFAULT_TABLE_PAGE_SIZE,
  TABLE_PAGE_SIZE_OPTIONS,
} from '../../../../../../utils/tableVariables';
import { useProgramTypeContext } from '../useProgramTypesContext';
import NoProgramDeliverableData from './NoProgramDeliverableData';

const ProgramTypesTable = () => {
  const [queryProgramTypes, { data, isLoading }] =
    useLazyGetApiProgramsDeliverableTypesQuery();
  const {
    payload,
    setPayload,
    setProgramDelType,
    programDelType,
    setChangeNumberModal,
    setUpdateTypeModal,
  } = useProgramTypeContext();
  // services-0.11.0 migrated this resource from PUT-whole-set (Pattern A) to
  // POST/DELETE per-row (Pattern B). Delete is by deliverableTypeId; the row's
  // composite uniqueness within a program is enforced server-side.
  const [deleteProgramType, { isLoading: deleting }] =
    useDeleteApiProgramsByIdDeliverableTypesAndDeliverableTypeIdMutation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<GridValidRowModel>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  const handleClose = () => {
    setOpenMenu(false);
    setAnchorEl(null);
  };

  useEffect(() => {
    (async () => {
      if (payload.id) {
        const data = await queryProgramTypes({
          programId: payload.id,
        }).unwrap();
        data && setProgramDelType(data.map((delTypes) => delTypes));
      }
    })();
  }, [queryProgramTypes, payload.id]);

  const handleDelete = useCallback(async () => {
    if (
      !payload.id
      || !selectedRow.deliverableTypeId
      || typeof selectedRow.deliverableNumber !== 'number'
    ) {
      setDeleteDialogOpen(false);
      return;
    }
    await deleteProgramType({
      id: payload.id,
      deliverableTypeId: selectedRow.deliverableTypeId,
      deliverableNumber: selectedRow.deliverableNumber,
    }).unwrap();
    const data = await queryProgramTypes({ programId: payload.id }).unwrap();
    setProgramDelType(data ?? []);
    setDeleteDialogOpen(false);
  }, [
    payload.id,
    selectedRow,
    deleteProgramType,
    queryProgramTypes,
    setProgramDelType,
  ]);

  return (
    <React.Fragment>
      <Paper sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <StyledDataGrid
          pagination
          pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
          initialState={{
            pagination: {
              paginationModel: { pageSize: DEFAULT_TABLE_PAGE_SIZE },
            },
          }}
          slotProps={{
            pagination: {
              showFirstButton: true,
              showLastButton: true,
            },
          }}
          rows={payload.id ? data : []}
          getRowId={(del) => del.deliverableNumber ?? ''}
          columns={[
            {
              field: 'deliverableNumber',
              headerName: 'Del #',
              flex: 0.5,
              valueGetter: (param: string) => param ?? '-',
            },
            {
              field: 'deliverableType',
              headerName: 'Type',
              flex: 2,
              valueGetter: (row: DbDeliverableType) => row?.title ?? '-',
            },
            {
              field: 'documentRef',
              headerName: 'Document Refs',
              flex: 2,
              valueGetter: (param: string) => (param?.length > 1 ? param : '-'),
            },
            {
              field: 'source',
              headerName: 'Source',
              flex: 0.5,
              valueGetter: (param: string) => (param?.length > 1 ? param : '-'),
            },
            {
              field: 'govRights',
              headerName: 'Gov Rights',
              flex: 0.5,
              valueGetter: (param: string) => (param?.length > 1 ? param : '-'),
            },
            {
              field: 'actions',
              type: 'actions',
              headerName: 'Actions',
              width: 100,
              cellClassName: 'actions',
              getActions: ({ row }) => {
                return [
                  <GridActionsCellItem
                    icon={
                      <MoreVert
                        sx={{
                          color: '#0092CB',

                          '&:hover': {
                            color: '#0091cbc9',
                          },
                        }}
                      />
                    }
                    label="Actions"
                    id="program-action-button"
                    onClick={(e) => {
                      setAnchorEl(e.currentTarget);
                      setOpenMenu(true);
                      setSelectedRow(row);
                    }}
                    color="inherit"
                  />,
                ];
              },
            },
          ]}
          slots={{
            noRowsOverlay: NoProgramDeliverableData,
          }}
          loading={isLoading}
        />

        <Menu anchorEl={anchorEl} open={openMenu} onClose={handleClose}>
          <MenuItem
            onClick={() => {
              handleClose();
              setUpdateTypeModal(true);
              setPayload((prev) => ({
                ...prev,
                body: [
                  selectedRow as HandlersCreateProgramDeliverableTypeRequest,
                ],
              }));
            }}
          >
            Update
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleClose();
              setDeleteDialogOpen(true);
            }}
          >
            Delete
          </MenuItem>
        </Menu>
      </Paper>
      <DeleteConfirmation
        open={deleteDialogOpen}
        title={`Remove ${selectedRow.deliverableType?.title}`}
        onClose={() => setDeleteDialogOpen(false)}
        content={{
          mainText: `Are you sure you want to delete <strong>${selectedRow.deliverableType?.title}</strong>? <br /><br />  This action cannot be undone.`,
        }}
        handleConfirmation={handleDelete}
        isLoading={deleting}
      />
    </React.Fragment>
  );
};

export default ProgramTypesTable;
